<template>
  <div
    class="nutrient-value"
    :class="[
      normClass,
      `nutrient-value--${variant}`,
      { 'nutrient-value--average': average },
    ]"
  >
    <div v-if="showMeasurement" class="measurement">({{ measurement || '-' }})</div>
    <div class="value-norm-icon">
      <div class="value">{{ value }}</div>
      <div v-if="showNorm" class="norm-icon">
        <div class="separator">/</div>
        <div class="norm">{{ norm }}</div>
        <Tooltip :placement="tooltipPlacement">
          <template #trigger>
            <div class="tooltip"><div class="icon" /></div>
          </template>
          <div
            class="nutrient-norm-tooltip"
            :class="[normClass, `nutrient-norm-tooltip--${variant}`]"
          >
            <div class="difference">
              <div class="icon" />
              <span class="difference-value">
                {{ positiveDifference ? '+' : '' }}
                {{ difference }}
                {{ measurement }}
              </span>
              ({{ percent }}
              %)
            </div>
            <div class="norm-title">Docelowa wartość</div>
            <div class="norm">{{ norm }} {{ measurement }}</div>
          </div>
        </Tooltip>
      </div>
      <slot name="after" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { Placement } from '@popperjs/core'
  import { computed } from 'vue'

  import { Tooltip } from '@/components/ui'

  import type { NormClass, NutrientMeasurement } from '@/utils/nutrients'

  const props = withDefaults(
    defineProps<{
      average?: boolean
      difference: number | string | undefined
      measurement: NutrientMeasurement
      norm: number
      normClass: NormClass
      percent: number | undefined
      showMeasurement?: boolean
      showNorm?: boolean
      showPositiveDifference?: boolean
      tooltipPlacement?: Placement
      value: number | string | undefined
      variant: 'day' | 'week'
    }>(),
    {
      average: false,
      showMeasurement: false,
      showNorm: true,
      showPositiveDifference: true,
      tooltipPlacement: 'top',
    },
  )

  const positiveDifference = computed(
    () => props.showPositiveDifference && Number(props.difference) > 0,
  )
</script>

<style scoped lang="scss">
  .nutrient-value {
    font-size: 14px;

    &--day {
      display: flex;
      flex-direction: column;

      .measurement {
        margin-bottom: 3px;
      }
    }

    &--week {
      .measurement {
        margin-bottom: 1px;
      }

      .value-norm-icon {
        align-items: center;
        height: 18px;
        line-height: 18px;
      }

      .norm-icon {
        align-items: center;
      }
    }

    &--average .value {
      font-family: Montserrat-SemiBold;
    }
  }

  .measurement {
    font-family: Montserrat-Medium;
    font-size: 9px;
    line-height: 10px;
    color: var(--color-tertiary-text);
  }

  .value,
  .norm {
    -webkit-touch-callout: none;
    user-select: none;
  }

  .value-norm-icon,
  .norm-icon {
    display: flex;
    flex-direction: row;
  }

  .separator {
    margin: 0 2px;
  }

  .norm {
    margin-right: 8px;
    color: var(--color-secondary-text);
  }

  .icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    cursor: pointer;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px 16px;
  }

  .red-up .icon,
  .red-down .icon {
    background-image: url('@/assets/img/diets/norm-red.svg?url');
  }

  .yellow-up .icon,
  .yellow-down .icon {
    background-image: url('@/assets/img/diets/norm-yellow.svg?url');
  }

  .yellow-down .icon,
  .red-down .icon {
    transform: rotate(180deg);
  }

  .ok .icon {
    width: 15px;
    height: 15px;
    background-image: url('@/assets/img/diets/norm-green.svg?url');
    background-size: 15px 15px;
  }

  .tooltip {
    width: max-content;
    height: 16px;
    text-align: center;
  }

  .nutrient-norm-tooltip {
    .difference {
      margin-bottom: 4px;
      font-family: Montserrat-Medium;
      font-size: 12px;
      line-height: 16px;

      .icon {
        width: 10px;
        height: 10px;
        margin-right: 8px;
        background-size: 10px 10px;
      }
    }

    &--week .difference {
      height: 16px;
    }

    &.red-up .difference-value,
    &.red-down .difference-value {
      color: var(--color-destructive-100);
    }

    &.yellow-up .difference-value,
    &.yellow-down .difference-value {
      color: var(--color-warning-100);
    }

    &.ok .difference-value {
      color: var(--color-success-100);
    }

    .norm-title {
      font-size: 10px;
      line-height: 13px;
      color: #c0c0c0;
    }

    .norm {
      font-family: Montserrat-Medium;
      font-size: 12px;
      line-height: 15px;
      color: var(--color-base-white);
    }
  }
</style>
